import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translationsAPI } from '../lib/api';

const defaultTranslations = {
  nl: {
    welcome: "Welkom",
    menu: "Menu",
    cart: "Winkelwagen",
    checkout: "Afrekenen",
    order_type: "Besteltype",
    delivery: "Bezorging",
    pickup: "Afhalen",
    dine_in: "Ter plaatse",
    your_order: "Uw bestelling",
    subtotal: "Subtotaal",
    delivery_fee: "Bezorgkosten",
    tip: "Fooi",
    total: "Totaal",
    place_order: "Bestelling plaatsen",
    name: "Naam",
    email: "E-mail",
    phone: "Telefoon",
    street: "Straat",
    house_number: "Huisnummer",
    city: "Stad",
    postal_code: "Postcode",
    additional_info: "Extra informatie",
    special_instructions: "Speciale instructies",
    add_to_cart: "Toevoegen",
    featured: "Aanbevolen",
    reserve_table: "Reserveren",
    party_size: "Aantal personen",
    date: "Datum",
    time: "Tijd",
    special_requests: "Speciale verzoeken",
    submit: "Verzenden",
    order_confirmed: "Bestelling bevestigd!",
    payment_pending: "Betaling in behandeling",
    back_to_home: "Terug naar home",
    min_order: "Minimum bestelling",
    delivery_not_available: "Bezorging niet beschikbaar voor deze postcode",
    order_number: "Bestelnummer",
    customer: "Klant",
    items: "Artikelen",
    print_ticket: "Bon printen",
    pending: "In afwachting",
    confirmed: "Bevestigd",
    preparing: "In bereiding",
    ready: "Klaar",
    delivered: "Bezorgd",
    cancelled: "Geannuleerd",
    enter_postal_code: "Voer uw postcode in",
    check_delivery: "Controleer bezorging",
    delivery_available: "Bezorging beschikbaar",
    address_required: "Volledig adres vereist",
    select_language: "Selecteer taal"
  },
  fr: {
    welcome: "Bienvenue",
    menu: "Menu",
    cart: "Panier",
    checkout: "Commander",
    order_type: "Type de commande",
    delivery: "Livraison",
    pickup: "À emporter",
    dine_in: "Sur place",
    your_order: "Votre commande",
    subtotal: "Sous-total",
    delivery_fee: "Frais de livraison",
    tip: "Pourboire",
    total: "Total",
    place_order: "Passer la commande",
    name: "Nom",
    email: "E-mail",
    phone: "Téléphone",
    street: "Rue",
    house_number: "Numéro",
    city: "Ville",
    postal_code: "Code postal",
    additional_info: "Informations supplémentaires",
    special_instructions: "Instructions spéciales",
    add_to_cart: "Ajouter",
    featured: "Recommandé",
    reserve_table: "Réserver",
    party_size: "Nombre de personnes",
    date: "Date",
    time: "Heure",
    special_requests: "Demandes spéciales",
    submit: "Envoyer",
    order_confirmed: "Commande confirmée!",
    payment_pending: "Paiement en attente",
    back_to_home: "Retour à l'accueil",
    min_order: "Commande minimum",
    delivery_not_available: "Livraison non disponible pour ce code postal",
    order_number: "Numéro de commande",
    customer: "Client",
    items: "Articles",
    print_ticket: "Imprimer ticket",
    pending: "En attente",
    confirmed: "Confirmé",
    preparing: "En préparation",
    ready: "Prêt",
    delivered: "Livré",
    cancelled: "Annulé",
    enter_postal_code: "Entrez votre code postal",
    check_delivery: "Vérifier la livraison",
    delivery_available: "Livraison disponible",
    address_required: "Adresse complète requise",
    select_language: "Choisir la langue"
  },
  en: {
    welcome: "Welcome",
    menu: "Menu",
    cart: "Cart",
    checkout: "Checkout",
    order_type: "Order Type",
    delivery: "Delivery",
    pickup: "Pickup",
    dine_in: "Dine In",
    your_order: "Your Order",
    subtotal: "Subtotal",
    delivery_fee: "Delivery Fee",
    tip: "Tip",
    total: "Total",
    place_order: "Place Order",
    name: "Name",
    email: "Email",
    phone: "Phone",
    street: "Street",
    house_number: "House Number",
    city: "City",
    postal_code: "Postal Code",
    additional_info: "Additional Info",
    special_instructions: "Special Instructions",
    add_to_cart: "Add",
    featured: "Featured",
    reserve_table: "Reserve",
    party_size: "Party Size",
    date: "Date",
    time: "Time",
    special_requests: "Special Requests",
    submit: "Submit",
    order_confirmed: "Order Confirmed!",
    payment_pending: "Payment Pending",
    back_to_home: "Back to Home",
    min_order: "Minimum Order",
    delivery_not_available: "Delivery not available for this postal code",
    order_number: "Order Number",
    customer: "Customer",
    items: "Items",
    print_ticket: "Print Ticket",
    pending: "Pending",
    confirmed: "Confirmed",
    preparing: "Preparing",
    ready: "Ready",
    delivered: "Delivered",
    cancelled: "Cancelled",
    enter_postal_code: "Enter your postal code",
    check_delivery: "Check delivery",
    delivery_available: "Delivery available",
    address_required: "Full address required",
    select_language: "Select language"
  }
};

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children, defaultLanguage = 'nl' }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('preferred_language') || defaultLanguage;
  });
  const [translations, setTranslations] = useState(defaultTranslations);

  useEffect(() => {
    localStorage.setItem('preferred_language', language);
  }, [language]);

  useEffect(() => {
    const fetchTranslations = async () => {
      try {
        const response = await translationsAPI.getAll();
        if (response.data) {
          setTranslations({ ...defaultTranslations, ...response.data });
        }
      } catch (error) {
        console.log('Using default translations');
      }
    };
    fetchTranslations();
  }, []);

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations.nl?.[key] || key;
  }, [translations, language]);

  const switchLanguage = useCallback((lang) => {
    if (['nl', 'fr', 'en'].includes(lang)) {
      setLanguage(lang);
    }
  }, []);

  return (
    <LanguageContext.Provider value={{
      language,
      translations: translations[language] || translations.nl,
      t,
      switchLanguage,
      availableLanguages: ['nl', 'fr', 'en'],
      languageNames: { nl: 'Nederlands', fr: 'Français', en: 'English' }
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
